/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';

import {
    Icon,
    useFlags,
    StyledModal,
    ModalActions,
    ButtonGroup,
    Button,
    DisplayHtmlContent,
    ContentBlock,
} from 'sarsaparilla';

import styles from './TopNavAlertBanner.module.scss';

type EnabledTopNavAlertBannerFlag = {
    enabled: true;
    bannerText: string;
    actionButtonText: string;
    modalHeader: string;
    modalBody: string;
    appearance?: 'info' | 'warning' | 'danger';
    /** White List: Only display banner on urls in the list. Matches the beginning of the provided paths, e.g. ["/camping"] matches "/camping/campground/123456" */
    urlWhiteList?: string[];
    /** Black List: Displays banners on all pages EXCEPT paths in the list. Matches the beginning of the provided paths, e.g. ["/camping"] matches "/camping/campground/123456" */
    urlBlackList?: string[];
};

type TopNavAlertBannerFlag =
    | {
          enabled: false;
      }
    | EnabledTopNavAlertBannerFlag;

export function TopNavAlertBanner() {
    const [isOpen, setIsOpen] = React.useState(false);
    const { topNavAlertBanner } = useFlags();

    if (!isEnabledTopNavAlertBannerFlag(topNavAlertBanner)) {
        return null;
    }

    if (!meetsUrlCriteria(topNavAlertBanner)) {
        return null;
    }

    const {
        appearance = 'warning',
        bannerText,
        actionButtonText = 'Read Announcement',
        modalHeader,
        modalBody,
    } = topNavAlertBanner;

    return (
        <>
            <section className={styles.banner}>
                <header className={cx(styles.header, styles[appearance])}>
                    <ContentBlock isFullWidth={true}>
                        <div className={styles.headerInner}>
                            <Icon
                                iconName={appearance === 'info' ? 'info' : 'warning'}
                                size="sm"
                                className={styles.warningIcon}
                            />
                            <div className={styles.textGroup}>
                                <div className={styles.bannerText}>{bannerText}</div>
                                <div className={styles.fauxButton}>
                                    <div className={styles.fauxButtonInner}>
                                        <span>{actionButtonText}</span>
                                        <Icon
                                            iconName={
                                                isOpen ? 'chevron-down' : 'chevron-right'
                                            }
                                            size="sm"
                                        />
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                className={styles.button}
                                aria-expanded={isOpen}
                                aria-controls="gov-banner"
                                onClick={() => {
                                    setIsOpen((prev) => !prev);
                                }}
                            >
                                <span className={styles.buttonText}>
                                    <span>{actionButtonText}</span>
                                    <Icon
                                        iconName={
                                            isOpen ? 'chevron-down' : 'chevron-right'
                                        }
                                        size="sm"
                                    />
                                </span>
                            </button>
                        </div>
                    </ContentBlock>
                </header>
            </section>

            <StyledModal
                isOpen={isOpen}
                onRequestClose={() => setIsOpen(false)}
                heading={modalHeader}
                shouldShowCloseButton={false}
                shouldCloseOnOverlayClick={true}
                shouldCloseOnEsc={true}
            >
                <DisplayHtmlContent html={modalBody} />

                <ModalActions>
                    <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                        <Button appearance="primary" onClick={() => setIsOpen(false)}>
                            Done
                        </Button>
                    </ButtonGroup>
                </ModalActions>
            </StyledModal>
        </>
    );
}

function isEnabledTopNavAlertBannerFlag(
    flag: TopNavAlertBannerFlag
): flag is EnabledTopNavAlertBannerFlag {
    return (
        flag?.enabled === true &&
        typeof flag?.bannerText === 'string' &&
        typeof flag?.modalHeader === 'string' &&
        typeof flag?.modalBody === 'string'
    );
}

function meetsUrlCriteria(flag: EnabledTopNavAlertBannerFlag) {
    // You can not have both a white list and a black list
    if (flag.urlWhiteList) {
        return meetsUrlWhiteListCriteria(flag.urlWhiteList);
    }

    if (flag.urlBlackList) {
        return meetsUrlBlackListCriteria(flag.urlBlackList);
    }

    return true;
}

/**
 * Checks if the current URL matches any URL in the whitelist.
 * - "/" matches only the root page exactly
 * - Other paths match if the current URL starts with that path (e.g., "/camping" matches "/camping/campground/123456")
 */
function meetsUrlWhiteListCriteria(whiteList: string[] | undefined) {
    if (!whiteList || !Array.isArray(whiteList)) {
        return true;
    }

    const currentUrl = window.location.pathname;
    return whiteList.some((url) => {
        // Special case: "/" should only match the root page exactly
        if (url === '/') {
            return currentUrl === '/';
        }
        // For other paths, match if current URL starts with the whitelist path
        return currentUrl.startsWith(url);
    });
}

/**
 * Checks if the current URL is NOT on the blacklist.
 * - "/" matches only the root page exactly
 * - Other paths match if the current URL starts with that path (e.g., "/camping" matches "/camping/campground/123456")
 */
function meetsUrlBlackListCriteria(blackList: string[] | undefined) {
    if (!blackList || !Array.isArray(blackList)) {
        return true;
    }

    const currentUrl = window.location.pathname;
    const isOnBlackList = blackList.some((url) => {
        // Special case: "/" should only match the root page exactly
        if (url === '/') {
            return currentUrl === '/';
        }
        // For other paths, match if current URL starts with the blacklist path
        return currentUrl.startsWith(url);
    });
    return !isOnBlackList;
}
